// src/pages/Signup.jsx
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, setDoc } from 'firebase/firestore';
import { FcGoogle } from 'react-icons/fc';
import PageContainer from '../components/PageContainer';

function Signup() {
  const { signup, loginWithGoogle, currentUser } = useAuth();
  const navigate = useNavigate();

  /* Toggle to show the email signup form vs. not */
  const [isEmailSignup, setIsEmailSignup] = useState(false);

  /* Role selection: 'artist' or 'engineer' */
  const [role, setRole] = useState('artist');

  // Shared fields for sign-up
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  /* Artist fields */
  const [artistFirstName, setArtistFirstName] = useState('');
  const [artistLastName, setArtistLastName] = useState('');
  const [artistAge, setArtistAge] = useState('');
  const [artistGender, setArtistGender] = useState('');
  const [artistGenre, setArtistGenre] = useState('');

  /* Engineer fields */
  const [engineerFirstName, setEngineerFirstName] = useState('');
  const [engineerLastName, setEngineerLastName] = useState('');
  const [engineerAge, setEngineerAge] = useState('');
  const [proficiency, setProficiency] = useState('Beginner Music Student');
  const [yearsExperience, setYearsExperience] = useState('');
  const [institution, setInstitution] = useState('');
  const [mixingGenres, setMixingGenres] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  /**
   * Validate the role-specific fields.
   */
  function validateFields() {
    if (role === 'artist') {
      if (!artistFirstName || !artistLastName || !artistAge || !artistGender || !artistGenre) {
        return 'Please fill out all artist fields (First Name, Last Name, Age, Gender, Genre).';
      }
      const ageNum = parseInt(artistAge, 10);
      if (ageNum < 14 || ageNum > 100) {
        return 'Artist Age must be between 14 and 100.';
      }
    } else if (role === 'engineer') {
      if (!engineerFirstName || !engineerLastName || !engineerAge || !yearsExperience || !mixingGenres) {
        return 'Please fill out all engineer fields (First Name, Last Name, Age, Years, Genres).';
      }
      const ageNum = parseInt(engineerAge, 10);
      if (ageNum < 14 || ageNum > 100) {
        return 'Engineer Age must be between 14 and 100.';
      }
      const expNum = parseInt(yearsExperience, 10);
      if (expNum < 0 || expNum > 100) {
        return 'Years of Mixing Experience must be between 0 and 100.';
      }
    }
    return null; // no errors
  }

  /**
   * Save user doc in Firestore. We'll store:
   * - role
   * - email
   * - name fields (artistFirstName/LastName or engineerFirstName/LastName)
   * - other relevant fields
   */
  async function saveUserDataToFirestore(uid, userEmail) {
    let userData = {
      role,
      email: userEmail,
      createdAt: new Date(),
    };

    if (role === 'artist') {
      userData = {
        ...userData,
        firstName: artistFirstName,
        lastName: artistLastName,
        age: parseInt(artistAge, 10) || null,
        gender: artistGender,
        genreOfMusic: artistGenre,
      };
    } else {
      // role === 'engineer'
      userData = {
        ...userData,
        firstName: engineerFirstName,
        lastName: engineerLastName,
        age: parseInt(engineerAge, 10) || null,
        proficiency,
        yearsExperience: parseInt(yearsExperience, 10) || 0,
        institution,
        genresExperienced: mixingGenres,
      };
    }

    await setDoc(doc(db, 'users', uid), userData, { merge: true });
  }

  /**
   * Email & Password sign-up flow
   */
  const handleEmailSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate role-based fields
    const validationError = validateFields();
    if (validationError) {
      setError(validationError);
      setLoading(false);
      return;
    }

    if (!email || !password) {
      setError('Email and Password are required.');
      setLoading(false);
      return;
    }

    try {
      const userCredential = await signup(email, password);
      const user = userCredential.user;

      // Save to Firestore with typed email
      await saveUserDataToFirestore(user.uid, user.email);

      alert('Signup successful!');
      navigate('/account');
    } catch (err) {
      console.error('Signup error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Google sign-up flow
   */
  const handleGoogleSignup = async () => {
    setLoading(true);
    setError('');

    // Validate role-based fields
    const validationError = validateFields();
    if (validationError) {
      setError(validationError);
      setLoading(false);
      return;
    }

    try {
      const result = await loginWithGoogle();
      const user = result.user;

      // user.email is the Gmail
      await saveUserDataToFirestore(user.uid, user.email);

      alert('Google signup successful!');
      navigate('/account');
    } catch (err) {
      console.error('Google signup error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // If user is already logged in
  if (currentUser) {
    return (
      <div className="page">
        <h2>You're already logged in!</h2>
        <button className="submit-button" onClick={() => navigate('/account')}>
          Go to Account
        </button>
      </div>
    );
  }

  return (
    <PageContainer>
      <h2>Sign Up</h2>
      <p>Please fill out your info and choose a sign-up method below.</p>

      <div className="music-form" style={{ maxWidth: 500, margin: '0 auto' }}>
        {/* Role Selector */}
        <label className="form-label">I am signing up as:</label>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <label>
            <input
              type="radio"
              name="role"
              value="artist"
              checked={role === 'artist'}
              onChange={(e) => setRole(e.target.value)}
            />
            Artist
          </label>
          <label>
            <input
              type="radio"
              name="role"
              value="engineer"
              checked={role === 'engineer'}
              onChange={(e) => setRole(e.target.value)}
            />
            Mixing Engineer
          </label>
        </div>

        {/* Artist Fields */}
        {role === 'artist' && (
          <>
            <label className="form-label" htmlFor="artistFirstName">
              First Name
            </label>
            <input
              type="text"
              id="artistFirstName"
              value={artistFirstName}
              onChange={(e) => setArtistFirstName(e.target.value)}
            />

            <label className="form-label" htmlFor="artistLastName">
              Last Name
            </label>
            <input
              type="text"
              id="artistLastName"
              value={artistLastName}
              onChange={(e) => setArtistLastName(e.target.value)}
            />

            <label className="form-label" htmlFor="artistAge">
              Age
            </label>
            <input
              type="number"
              id="artistAge"
              value={artistAge}
              onChange={(e) => setArtistAge(e.target.value)}
              placeholder="14 - 100"
            />

            <label className="form-label" htmlFor="artistGender">
              Gender
            </label>
            <select
              id="artistGender"
              value={artistGender}
              onChange={(e) => setArtistGender(e.target.value)}
            >
              <option value="">Select Gender</option>
              <option value="Female">Female</option>
              <option value="Male">Male</option>
              <option value="Non-binary">Non-binary</option>
              <option value="Prefer not to say">Prefer not to say</option>
            </select>

            <label className="form-label" htmlFor="artistGenre">
              Genre of Music
            </label>
            <input
              type="text"
              id="artistGenre"
              value={artistGenre}
              onChange={(e) => setArtistGenre(e.target.value)}
              placeholder="e.g. Hip-Hop, Rock, EDM..."
            />
          </>
        )}

        {/* Engineer Fields */}
        {role === 'engineer' && (
          <>
            <label className="form-label" htmlFor="engineerFirstName">
              First Name
            </label>
            <input
              type="text"
              id="engineerFirstName"
              value={engineerFirstName}
              onChange={(e) => setEngineerFirstName(e.target.value)}
            />

            <label className="form-label" htmlFor="engineerLastName">
              Last Name
            </label>
            <input
              type="text"
              id="engineerLastName"
              value={engineerLastName}
              onChange={(e) => setEngineerLastName(e.target.value)}
            />

            <label className="form-label" htmlFor="engineerAge">
              Age
            </label>
            <input
              type="number"
              id="engineerAge"
              value={engineerAge}
              onChange={(e) => setEngineerAge(e.target.value)}
              placeholder="14 - 100"
            />

            <label className="form-label">Proficiency Level</label>
            <select
              value={proficiency}
              onChange={(e) => setProficiency(e.target.value)}
            >
              <option value="Beginner Music Student">Beginner Music Student</option>
              <option value="Intermediate Student">Intermediate Student</option>
              <option value="Advanced Student">Advanced Student</option>
              <option value="Self Trained Beginner">Self Trained Beginner</option>
              <option value="Self Trained Intermediate">Self Trained Intermediate</option>
              <option value="Self Trained Advanced">Self Trained Advanced</option>
              <option value="Professional">Professional</option>
            </select>

            <label className="form-label" htmlFor="yearsExperience">
              Years of Mixing Experience
            </label>
            <input
              type="number"
              id="yearsExperience"
              value={yearsExperience}
              onChange={(e) => setYearsExperience(e.target.value)}
              placeholder="0 - 100"
            />

            <label className="form-label" htmlFor="institution">
              Institution (Optional)
            </label>
            <input
              type="text"
              id="institution"
              value={institution}
              onChange={(e) => setInstitution(e.target.value)}
              placeholder="Music school, employer, or self-taught"
            />

            <label className="form-label" htmlFor="mixingGenres">
              Genres You Have Experience Mixing
            </label>
            <input
              type="text"
              id="mixingGenres"
              value={mixingGenres}
              onChange={(e) => setMixingGenres(e.target.value)}
              placeholder="e.g. Hip-Hop, Rock, Jazz..."
            />
          </>
        )}

        {/* Show Email/Pass if user chooses to sign up with Email */}
        {isEmailSignup && (
          <>
            <hr style={{ margin: '1rem 0' }} />

            <label className="form-label" htmlFor="email">
              Email
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <label className="form-label" htmlFor="password">
              Password
            </label>
            <input
              type="password"
              id="password"
              minLength="6"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            <button
              className="submit-button"
              onClick={handleEmailSignup}
              disabled={loading}
              type="button"
            >
              {loading ? 'Signing Up...' : 'Sign Up (Email/Pass)'}
            </button>
          </>
        )}

        {error && (
          <p style={{ color: 'red', marginTop: '1rem' }}>{error}</p>
        )}
      </div>

      <div className="button-container" style={{ marginTop: '2rem' }}>
        {!isEmailSignup && (
          <button
            className="submit-button"
            onClick={() => setIsEmailSignup(true)}
            disabled={loading}
          >
            Sign Up with Email
          </button>
        )}

        <button
          className="submit-button"
          onClick={handleGoogleSignup}
          disabled={loading}
        >
          <FcGoogle style={{ marginRight: '8px' }} />
          Sign Up with Google
        </button>
      </div>
    </PageContainer>
  );
}

export default Signup;
